import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from "@angular/core";
import {CustomEqualitySet} from "custom-equality-set";
import {Money} from "money";
import {CartService} from "purchasing-shared/cart.service";
import {contextPath} from "site";
import {purchasingEnabled} from "user";
import {Aircraft} from "../aircraft";
import {AvdbBundleCartItem} from "../avdb-bundle-cart-item";
import {AvdbCartItem} from "../avdb-cart-item";
import {AvdbType} from "../avdb-type";
import {Device} from "../device";
import {AvdbPricingAircraftBundle} from "./avdb-pricing-aircraft-bundle";
import {AvdbPricingBundle} from "./avdb-pricing-bundle";
import {AvdbPricingBundleCoverage} from "./avdb-pricing-bundle-coverage";
import {AvdbPricingBundleCoveragePackage} from "./avdb-pricing-bundle-coverage-package";
import {AvdbPricingDeviceBundle} from "./avdb-pricing-device-bundle";
import {AvdbPricingProduct} from "./avdb-pricing-product";
import {AvdbPricingResponse} from "./avdb-pricing-response";
import {AvdbPricingSelectedProduct} from "./avdb-pricing-selected-product";
import {KeepShoppingModalComponent} from "./keep-shopping-modal.component";
import {CartItem} from "../../purchasing/cart/cart-item";
import {Observable} from "rxjs";

@Component({
	selector: "selected-products",
	template: `
		<!---------- SELECTED PRODUCTS / RECOMMENDED BUNDLE SECTION ---------->
		<ng-container>

			<div *ngIf="!lowerCostBundleCoveragePackageExists(); else recommendedBundleHeadline"
					class="databasepricing_bundlecomparison_headline">
				<h3>Selected Products</h3>
			</div>

			<!---------- RECOMMENDED BUNDLE - HEADLINE ---------->
			<ng-template #recommendedBundleHeadline>
				<div class="databasepricing_bundlecomparison_headline">
					<h3>We recommend the <strong>{{getRecommendedBundleCoveragePackage().bundle.description}}.</strong></h3>
					<p>The databases you selected are available at a lower price as a part of a bundle.</p>
				</div>
			</ng-template>

			<!---------- RECOMMENDED BUNDLE - COMPARISON TABLE ---------->
			<div class="databasepricing_comparisontable">
				<div class="databasepricing_comparisontable_headerfooterrow">
					<div class="databasepricing_comparisontable_blankspan"></div>
					<div class="databasepricing_comparisontable_priceinfo"><strong>Your Selections</strong></div>
					<div class="databasepricing_comparisontable_priceinfo" *ngIf="lowerCostBundleCoveragePackageExists()">
						<strong>{{getRecommendedBundleCoveragePackage().bundle.description}}<br>
							{{getRecommendedBundleCoveragePackage().bundle.price | money}}
						</strong>
					</div>
				</div>

				<!---------- RECOMMENDED BUNDLE - LIST OF PRODUCTS ---------->
				<ng-container *ngFor="let device of allSelectedDevices">
					<ng-container *ngFor="let avdbType of getAvdbTypesForDevice(device)">
						<bundle-comparison-row
							[deviceName]="device.name"
							[avdbTypeName]="avdbType.name"
							[products]="getSelectedAndIncludedProductsForDeviceAndAvdbTypeID(device, avdbType.id)"
							[recommendedPackage]="getRecommendedBundleCoveragePackage()">
						</bundle-comparison-row>
					</ng-container>
				</ng-container>

				<!---------- RECOMMENDED BUNDLE - TOTAL COSTS ---------->
				<div class="databasepricing_comparisontable_headerfooterrow">
					<div class="databasepricing_comparisontable_blankspan"></div>
					<div class="databasepricing_comparisontable_priceinfo">
						<strong>{{getTotalPriceOfSelectedProducts() | money}}</strong>
					</div>
					<div class="databasepricing_comparisontable_priceinfo" *ngIf="lowerCostBundleCoveragePackageExists()">
						<strong>{{getRecommendedBundleCoveragePackage().totalPrice | money}}</strong><br>
						<small>{{getPercentSavingsDisplayForRecommendedBundleCoveragePackage()}}</small>
					</div>
				</div>

				<!---------- RECOMMENDED BUNDLE - ADD TO CART BUTTONS ---------->
				<div class="databasepricing_comparisontable_headerfooterrow">
					<div class="databasepricing_comparisontable_blankspan"></div>
					<div class="databasepricing_comparisontable_priceinfo">
						<ng-container *ngIf="loadingAddToCart">
							<button class="button button-secondary" [disabled]="loadingAddToCart">
								Add to Cart
							</button>
						</ng-container>
						<ng-container *ngIf="!loadingAddToCart">
							<button class="button button-secondary" (click)="addSelectedProductsToCart()">
								Add to Cart
							</button>
						</ng-container>
					</div>

					<div class="databasepricing_comparisontable_priceinfo" *ngIf="userPurchasingEnabled && lowerCostBundleCoveragePackageExists()">
						<ng-container *ngIf="loadingAddToCart">
							<button class="button button-primary" [disabled]="loadingAddToCart">
								Add to Cart
							</button>
						</ng-container>
						<ng-container *ngIf="!loadingAddToCart">
							<button class="button button-primary" (click)="addRecommendedBundleCoveragePackageToCart()">
								Add to Cart
							</button>
						</ng-container>
					</div>
				</div>
			</div>

			<keep-shopping-modal (continueShopping)="continueShopping.emit()"></keep-shopping-modal>

		</ng-container>
	`,
})
export class SelectedProductsComponent implements OnChanges {

	contextPath = contextPath;

	@Input() selectedAircraft: Aircraft;
	@Input() selectedPortable: Device;
	@Input() partKeyProductMap = new Map<string, AvdbPricingProduct>();
	@Input() avdbPricingResponse: AvdbPricingResponse;
	@Input() selectedProducts = new CustomEqualitySet<AvdbPricingSelectedProduct>();
	@Output() invalidCartAdd = new EventEmitter();
	@Output() continueShopping = new EventEmitter();

	@ViewChild(KeepShoppingModalComponent) keepShoppingModal: KeepShoppingModalComponent;

	// Bundle Coverages / Packages
	bundleCoverages: Array<AvdbPricingBundleCoverage> = [];
	bundleCoveragePackages: Array<AvdbPricingBundleCoveragePackage> = [];
	selectedAndIncludedProducts: Array<AvdbPricingSelectedProduct> = [];

	loadingAddToCart: boolean = false;

	readonly userPurchasingEnabled = purchasingEnabled;

	constructor(
			private cartService: CartService) { }

	ngOnChanges(changes: SimpleChanges) {

		if ("selectedProducts" in changes) {
			this.calculateBundleCoverages();
		}
	}

	// TODO: Duplicated from DatabasePricingComponent
	get allSelectedDevices(): Array<Device> {
		let devices: Array<Device> = [];
		if (this.selectedAircraft != null)
			devices.push(...this.selectedAircraft.devices);
		if (this.selectedPortable != null)
			devices.push(this.selectedPortable);
		return devices;
	}

	getAvdbTypesForDevice(device: Device): Array<AvdbType> {
		return _.sortBy(device.avdbTypes, avdbType => avdbType.displayOrder);
	}

	getTotalPriceOfSelectedProducts(): Money {
		return Money.sum(Array.from(this.selectedProducts).map(p => p.product.price));
	}

	// ====================================================================================================
	// Bundles
	// ====================================================================================================

	private isProductCoveredByBundle(bundle: AvdbPricingBundle, deviceModelID: number, product: AvdbPricingProduct) {
		const bundleAvdbTypes = bundle.avdbTypesForDeviceModelID(deviceModelID);
		const bundleAvdbType = bundleAvdbTypes.find(bundleAvdbType => bundleAvdbType.id === product.productType.id);
		if (!bundleAvdbType)
			return false;

		return bundleAvdbType.productOptions.includes(product.partKey);
	}

	private getIncludedProductsForDeviceFromBundle(device: Device, bundle: AvdbPricingBundle): Array<AvdbPricingSelectedProduct> {
		const includedProducts = new CustomEqualitySet<AvdbPricingSelectedProduct>();

		const avdbTypes = bundle.avdbTypesForDeviceModelID(device.modelID);
		avdbTypes.forEach(deviceBundleAvdbType => {
			deviceBundleAvdbType.productOptions.forEach(partKey => {
				const product = this.partKeyProductMap.get(partKey);
				if (product != null) {
					includedProducts.add(new AvdbPricingSelectedProduct(product, device, false));
				}
			});
		});

		return Array.from(includedProducts);
	}

	// ====================================================================================================
	// Aircraft Bundles (OnePaks and PilotPaks)
	// ====================================================================================================

	getAircraftBundlesThatIncludeProductForDevice(product: AvdbPricingProduct, device: Device): Array<AvdbPricingAircraftBundle> {
		if (!this.avdbPricingResponse)
			return [];

		return this.avdbPricingResponse.aircraftBundles
			.filter(bundle => this.isProductCoveredByBundle(bundle, device.modelID, product));
	}

	// ====================================================================================================
	// Device Bundles
	// ====================================================================================================

	getDeviceBundlesThatIncludeProductForDevice(product: AvdbPricingProduct, device: Device): Array<AvdbPricingDeviceBundle> {
		if (!this.avdbPricingResponse)
			return [];

		let devicePricing = this.avdbPricingResponse.devices.find(d => d.modelID === device.modelID);
		if (devicePricing == null)
			return [];

		return devicePricing.bundles
			.filter(bundle => this.isProductCoveredByBundle(bundle, device.modelID, product));
	}

	// ====================================================================================================
	// Bundle Coverages
	// ====================================================================================================

	private calculateBundleCoverages() {
		const bundleCoverages = new CustomEqualitySet<AvdbPricingBundleCoverage>();

		this.selectedProducts.forEach(selectedProduct => {
			const aircraftBundles = this.getAircraftBundlesThatIncludeProductForDevice(selectedProduct.product, selectedProduct.device);
			const deviceBundles = this.getDeviceBundlesThatIncludeProductForDevice(selectedProduct.product, selectedProduct.device);
			const bundles = [].concat(aircraftBundles, deviceBundles);
			bundles.forEach(bundle => {
				const deviceBundleCoverage = this.getBundleCoverage(bundle);
				bundleCoverages.add(deviceBundleCoverage);
			});
		});

		this.bundleCoverages = _.sortByOrder(Array.from(bundleCoverages),
			[b => b.bundlePrice, b => b.numberOfCoveredSelectedProducts],
			["asc", "desc"]);

		this.calculateBundleCoveragePackages();
	}

	private getBundleCoverage(bundle: AvdbPricingBundle): AvdbPricingBundleCoverage {
		let coveredSelectedProducts = Array.from(this.selectedProducts)
			.filter(selectedProduct => this.isProductCoveredByBundle(bundle, selectedProduct.device.modelID, selectedProduct.product));

		for (let device of this.allSelectedDevices) {
			for (let avdbType of this.getAvdbTypesForDevice(device)) {
				let selectedProducts = coveredSelectedProducts.filter(p => p.device.id === device.id && p.product.productType.id === avdbType.id);

				// If there is more than 1 covered and selected product for this device and AVDB type,
				// keep the highest-priced product, but remove the rest from the covered products array.
				if (selectedProducts.length > 1) {
					selectedProducts = _.sortByOrder(selectedProducts, [p => p.product.price.value], ["desc"]);
					const productsToRemove = selectedProducts.slice(1, selectedProducts.length);
					coveredSelectedProducts = coveredSelectedProducts.filter(p => !productsToRemove.includes(p));
				}
			}
		}

		return new AvdbPricingBundleCoverage(bundle, coveredSelectedProducts);
	}

	private getMissingSelectedProductsForBundleCoverage(bundleCoverage: AvdbPricingBundleCoverage): Array<AvdbPricingSelectedProduct> {
		const missingSelectedProducts = Array.from(this.selectedProducts).filter(selectedProduct => bundleCoverage.coveredSelectedProducts.indexOf(selectedProduct) < 0);
		return _.sortBy(missingSelectedProducts, selectedProduct => selectedProduct.device.name);
	}

	// ====================================================================================================
	// Bundle Coverage Packages
	// ====================================================================================================

	private calculateBundleCoveragePackages() {
		if (!this.avdbPricingResponse) {
			this.bundleCoveragePackages = [];
			return;
		}

		const bundleCoveragePackages: Array<AvdbPricingBundleCoveragePackage> = [];
		this.bundleCoverages.forEach(bundleCoverage => {
			const additionalSelectedProducts = this.getMissingSelectedProductsForBundleCoverage(bundleCoverage);
			bundleCoveragePackages.push(new AvdbPricingBundleCoveragePackage(bundleCoverage, additionalSelectedProducts));
		});

		this.bundleCoveragePackages = _.sortBy(bundleCoveragePackages, bcp => bcp.totalPrice.value);
		this.calculateSelectedAndIncludedProductsForRecommendedBundleCoveragePackage();
	}

	lowerCostBundleCoveragePackageExists(): boolean {
		if (this.bundleCoveragePackages.length === 0)
			return false;

		const selectedProductsTotalPrice = this.getTotalPriceOfSelectedProducts();
		const bundlePackage = this.bundleCoveragePackages[0]; // bundleCoveragePackages already sorted by lowest price

		return (bundlePackage.totalPrice.value < selectedProductsTotalPrice.value);
	}

	getRecommendedBundleCoveragePackage(): AvdbPricingBundleCoveragePackage {
		if (this.lowerCostBundleCoveragePackageExists()) {
			return this.bundleCoveragePackages[0]; // bundleCoveragePackages already sorted by lowest price
		}
		else {
			return undefined;
		}
	}

	getPercentSavingsDisplayForRecommendedBundleCoveragePackage(): string {
		const selectedProductsTotalPrice = this.getTotalPriceOfSelectedProducts();
		const bundlePackageTotalPrice = this.getRecommendedBundleCoveragePackage().totalPrice;
		const percent = 100 - (Math.floor(bundlePackageTotalPrice.value / selectedProductsTotalPrice.value * 100));
		return `Save ${percent}%`;
	}

	private calculateSelectedAndIncludedProductsForRecommendedBundleCoveragePackage() {
		let products = new CustomEqualitySet(this.selectedProducts);

		if (this.lowerCostBundleCoveragePackageExists()) {
			const bundleCoveragePackage = this.getRecommendedBundleCoveragePackage();

			// Grab all included products for each device from the bundle (not user-selected)
			let includedProducts: Array<AvdbPricingSelectedProduct> = [];
			if (this.selectedAircraft) {
				this.selectedAircraft.devices.forEach(device => {
					includedProducts = includedProducts.concat(this.getIncludedProductsForDeviceFromBundle(device, bundleCoveragePackage.bundle));
				});
			}
			if (this.selectedPortable) {
				includedProducts = includedProducts.concat(this.getIncludedProductsForDeviceFromBundle(this.selectedPortable, bundleCoveragePackage.bundle));
			}

			// Combine into one array of products (selected and included)
			includedProducts.forEach(includedProduct => {
				products.add(includedProduct);
			});
		}

		this.selectedAndIncludedProducts = _.sortByAll(Array.from(products),
			p => p.device.name,
			p => AvdbType.displayOrder(p.product.productType.id),
			p => p.product.series[0].region.name,
			p => p.product.numberOfIssues);
	}

	getSelectedAndIncludedProductsForDeviceAndAvdbTypeID(device: Device, avdbTypeID: number): Array<AvdbPricingSelectedProduct> {
		return this.selectedAndIncludedProducts.filter(product => {
			return (product.device.id === device.id) && (product.product.productType.id === avdbTypeID);
		});
	}

	// ====================================================================================================
	// Cart
	// ====================================================================================================

	addSelectedProductsToCart() {
		if (this.userPurchasingEnabled) {
			this.loadingAddToCart = true;
			for (let selection of Array.from(this.selectedProducts)) {
				let cartItem = new AvdbCartItem({product: selection.product, device: selection.device});
				this.cartService.addItem(cartItem).subscribe(() => {
					this.keepShoppingModal.addModalItem(cartItem);
					this.keepShoppingModal.open();
					this.loadingAddToCart = false;
				});
			}
		}
		else {
			this.invalidCartAdd.emit();
		}
	}

	addRecommendedBundleCoveragePackageToCart() {
		let bundleCoveragePackage = this.getRecommendedBundleCoveragePackage();
		this.loadingAddToCart = true;
		let itemsToAdd: CartItem[] = [];

		// Add main bundle
		if (bundleCoveragePackage.bundle instanceof AvdbPricingAircraftBundle) {
			let aircraft = this.selectedAircraft;
			let cartItem = new AvdbBundleCartItem({bundle: bundleCoveragePackage.bundle, aircraft});
			itemsToAdd.push(cartItem);
		}
		else if (bundleCoveragePackage.bundle instanceof AvdbPricingDeviceBundle) {
			let bundle = bundleCoveragePackage.bundle;
			let devices = this.allSelectedDevices.filter(d => d.modelID === bundle.deviceModelID);
			for (let device of devices) {
				let cartItem = new AvdbBundleCartItem({bundle, device});
				itemsToAdd.push(cartItem);
			}
		}

		// Add selected products that are not covered by bundle
		for (let selection of bundleCoveragePackage.additionalSelectedProducts) {
			let cartItem = new AvdbCartItem({product: selection.product, device: selection.device});
			itemsToAdd.push(cartItem);
		}

		Observable.forkJoin(itemsToAdd.map(item => this.cartService.addItem(item))).subscribe(_ => {
			itemsToAdd.map(item => this.keepShoppingModal.addModalItem(item));
			this.keepShoppingModal.open();
			this.loadingAddToCart = false
		});
	}
}
